import { Component, OnInit, signal } from '@angular/core';
import { UserUnitsModel } from './user-units.model';
import { AppNotificationService } from 'src/app/shared/services/app-notification.service';
import { lastValueFrom } from 'rxjs';
import { getSelectList, doglegSeverity, shortLengths, longLengths, torque, 
  temperatureGradient, linearDensity, temperature, pressure, stressPressure, coefficientOfThermalExpansion,
  thermalConductivity, gasInjectionRate, solidsDensity, slurryFlow, massFlow, gasGradient, enthalpy,
  force, speed, density,volume,hydrocarbonFlow, area, standardFluidFlow, specificHeatCapacity, heat,
  heatTransferCoefficient, pressureLoss, power, yieldPoint, plasticViscosity,
  kPrime
 } from '../../services/unit-library';
import { Router } from '@angular/router';
import { UsersService } from '../../services/users-service';
import { StorageKeys, StoreService } from '../../services/store.service';
import { UnitService } from '../../services/unit.service';

@Component({
    selector: 'app-user-units',
    templateUrl: './user-units.component.html',
    providers: [UsersService],
    standalone: false
})
export class UserUnitsComponent implements OnInit {
  public units: UserUnitsModel;
  public isLoading = signal<boolean>(true);
  
  public readonly shortLengthUnitOptions = getSelectList(shortLengths());
  public readonly longLengthUnitOptions = getSelectList(longLengths());
  public readonly dlsUnitOptions = getSelectList(doglegSeverity());
  public readonly tempGradientOptions = getSelectList(temperatureGradient());
  public readonly linearDensityUnitOptions = getSelectList(linearDensity());
  public readonly temperatureUnitOptions = getSelectList(temperature());
  public readonly pressureUnitOptions = getSelectList(pressure());
  public readonly stressPressureUnitOptions = getSelectList(stressPressure());
  public readonly forceUnitOptions = getSelectList(force());
  public readonly speedUnitOptions = getSelectList(speed());
  public readonly densityUnitOptions = getSelectList(density());
  public readonly standardFluidFlowUnitOptions = getSelectList(standardFluidFlow());
  public readonly hydrocarbonFlowUnitOptions = getSelectList(hydrocarbonFlow());
  public readonly volumeUnitOptions = getSelectList(volume());
  public readonly areaUnitOptions = getSelectList(area());
  public readonly thermalConductivityUnitOptions = getSelectList(thermalConductivity());
  public readonly thermalExpansionCoeffUnitOptions = getSelectList(coefficientOfThermalExpansion());
  public readonly gasInjectionRateUnitOptions = getSelectList(gasInjectionRate());
  public readonly solidsDensityUnitOptions = getSelectList(solidsDensity());
  public readonly massFlowOptions = getSelectList(massFlow());
  public readonly specificHeatCapacityUnitOptions = getSelectList(specificHeatCapacity());
  public readonly slurryFlowRateOptions = getSelectList(slurryFlow());
  public readonly gasGradientOptions = getSelectList(gasGradient());
  public readonly enthalpyOptions = getSelectList(enthalpy());
  public readonly heatOptions = getSelectList(heat());
  public readonly torqueOptions = getSelectList(torque());
  public readonly heatTransferCoefficientOptions = getSelectList(heatTransferCoefficient());
  public readonly pressureLossOptions = getSelectList(pressureLoss());
  public readonly powerUnitOptions = getSelectList(power());
  public readonly yieldPointUnitOptions = getSelectList(yieldPoint());
  public readonly plasticViscosityUnitOptions = getSelectList(plasticViscosity());
  public readonly kPrimeUnitOptions = getSelectList(kPrime());

  public searchTerm: string = '';

  constructor(
    private _usersService: UsersService,
    private _toaster: AppNotificationService,
    private _storeService: StoreService,
    private _unitService: UnitService,
    private _router: Router
  ) { }

  async ngOnInit() : Promise<void> {
    this.isLoading.set(true);
    this.units = await this._storeService.get(StorageKeys.UNITS) || new UserUnitsModel();
    if (this.units) {
      this.isLoading.set(false);
    }
  }

  async onUserMetadataFormSubmit() {
    await this.saveUnits();
    this._router.navigateByUrl('/designs');
  }

  async onApply() {
    const updated = await this.saveUnits();
    if (updated) {
      await this._unitService.refresh();
    } else {
      this._toaster.showError('Units could not be updated');
    }
  }

  private async saveUnits(): Promise<boolean> {
    const updated = await lastValueFrom(this._usersService.updateUserUnits(this.units));
    if (!updated) {
      return false;
    }

    await this._storeService.set(StorageKeys.UNITS, this.units);
    this._toaster.showSuccess('Units updated');
    return true;
  }

  async onCancel() {
    this._router.navigateByUrl('/designs');
  }

  public resetToApi() {
    this.units = {
      longLengths: 'ft',
      shortLengths: 'in',
      doglegSeverity: 'd-ft',
      temperatureGradient: '∆°F/ft',
      linearDensity: 'lb/ft',
      temperature: '°F',
      pressure: 'psi',
      stressPressure: 'psi',
      force: 'lbf',
      speed: 'ft/s',
      density: 'ppg (U.S.)',
      standardFluidFlow: 'GPM',
      hydrocarbonFlow: 'bbl/d',
      volume: 'bbl',
      area: 'in²',
      thermalConductivity: 'BTU/h·ft·°F',
      coefficientOfThermalExpansion: '1/°F',
      gasInjectionRate: 'scfh',
      solidsDensity: 'lbm/ft³',
      massFlow: 'lb/h',
      specificHeatCapacity: 'BTU/ft³·°F',
      slurryFlow: 'bbl/min',
      gasGradient: 'psi/ft',
      enthalpy: 'BTU/lbm',
      heat: 'BTU',
      torque: 'lbf·ft',
      heatTransferCoefficient: 'Btu/ft²·°F',
      pressureLoss: 'psi/100ft',
      power: 'hp(I)',
      yieldPoint: 'lbf/100ft²',
      plasticViscosity: 'cP',
      kPrime: "lb*s^n'/ft²"
    };
  }

  public resetToSi() {
    this.units = {
      longLengths: 'm',
      shortLengths: 'in',
      doglegSeverity: 'd-m',
      temperatureGradient: '∆°C/m',
      linearDensity: 'kg/m',
      temperature: '°C',
      pressure: 'bar',
      stressPressure: 'KPa',
      force: 'tf',
      speed: 'm/s',
      density: 'kg/m³',
      standardFluidFlow: 'm³/min',
      hydrocarbonFlow: 'm³/d',
      volume: 'm³',
      area: 'cm²',
      thermalConductivity: 'W/m·K',
      coefficientOfThermalExpansion: '1/°C',
      gasInjectionRate: 'Sm³/h',
      solidsDensity: 'kg/m³',
      massFlow: 't/d',
      specificHeatCapacity: 'J/m³·°C',
      slurryFlow: 'm³/min',
      gasGradient: 'bar/m',
      enthalpy: 'J/kg',
      heat: 'J',
      torque: 'N·m',
      heatTransferCoefficient: 'W/m²·K',
      pressureLoss: 'bar/1000m',
      power: 'Btu/h',
      yieldPoint: 'Pa',
      plasticViscosity: 'Pa/s',
      kPrime: "Pa*s^n"
    };
  }

  public resetToMix() {
    this.units = {
      longLengths: 'm',
      shortLengths: 'in',
      doglegSeverity: 'd-m',
      temperatureGradient: '∆°C/m',
      linearDensity: 'lb/ft',
      temperature: '°C',
      pressure: 'bar',
      stressPressure: 'psi',
      force: 'lbf',
      speed: 'm/s',
      density: 'sg',
      standardFluidFlow: 'GPM',
      hydrocarbonFlow: 'bbl/d',
      volume: 'bbl',
      area: 'in²',
      thermalConductivity: 'BTU/h·ft·°F',
      coefficientOfThermalExpansion: '1/°C',
      gasInjectionRate: 'scfh',
      solidsDensity: 'lbm/ft³',
      massFlow: 'lb/h',
      specificHeatCapacity: 'BTU/ft³·°F',
      slurryFlow: 'bbl/min',
      gasGradient: 'psi/ft',
      enthalpy: 'BTU/lbm',
      heat: 'BTU',
      torque: 'lbf·ft',
      heatTransferCoefficient: 'Btu/ft²·°F',
      pressureLoss: 'psi/100ft',
      power: 'hp(I)',
      yieldPoint: 'lbf/100ft²',
      plasticViscosity: 'cP',
      kPrime: "lb*s^n'/ft²"
    };
  }

  public shouldShowField(fieldLabel: string): boolean {
    if (!this.searchTerm.trim()) {
      return true;
    }
    return fieldLabel.toLowerCase().includes(this.searchTerm.toLowerCase());
  }
}
